import { type DateRange } from "~/stores/dateRangeStore"
import { userAdjustments } from "~/utils/adjustmentsUtility"
import { getYearUTC, startOfDayUTC } from "~/utils/datetime"

import { type PlotBand, type PlotLine } from "../SuperChart"
import { type IVariantCollection } from "~/hooks/useCollections"
import { getTailwindValue } from "~/utils/tailwind"

export const createVerticalLine = (date: Date, lineColor: string, text?: string): PlotLine => {
  const plotLine: PlotLine = {
    text: text ?? "",
    value: date.getTime(),
    lineColor,
    width: 1,
  }
  if (text) {
    plotLine.backgroundColor = lineColor
    plotLine.textColor = "#21212E"
  }

  return plotLine
}

export const createYearPlotLines = (dateRange: DateRange) => {
  const startYear = getYearUTC(dateRange.start)
  const endYear = getYearUTC(dateRange.end)
  const out: PlotLine[] = []

  for (let year = startYear; year <= endYear; year++) {
    out.push(
      createVerticalLine(startOfDayUTC(new Date(year.toString())), "rgba(156, 157, 157, 0.6)"),
    )
  }

  return out
}

type OverlappingAdjustments = {
  adjustments: { name: string; startDate: Date; endDate: Date; isSystem: boolean }[]
}[]

type BandRange = { text: string; from: number; to: number; yearsShifted?: number }

function addYearsUTC(date: Date, years: number) {
  const out = new Date(date.getTime())
  out.setUTCFullYear(out.getUTCFullYear() + years)
  return out
}

export function getAdjustmentBandRanges(
  rows: OverlappingAdjustments,
  overlayWindowStart?: Date,
): BandRange[] {
  return userAdjustments(rows).map((a) => {
    let { startDate, endDate } = a
    let text = a.name
    // Adjustments that ended before the visible window belong to the overlaid
    // past-year sales series. Shift them forward by whole years — mirroring how
    // the overlay series are positioned on the axis — so the campaign flag sits
    // on the peak it caused, and tag the label with the original year so it
    // can't be read as an adjustment on the current period.
    let yearsShifted = 0
    if (overlayWindowStart && endDate < overlayWindowStart) {
      const originalYear = getYearUTC(startDate)
      while (endDate < overlayWindowStart) {
        startDate = addYearsUTC(startDate, 1)
        endDate = addYearsUTC(endDate, 1)
        yearsShifted += 1
      }
      text = `${text} (${originalYear})`
    }
    return { text, from: startDate.getTime(), to: endDate.getTime(), yearsShifted }
  })
}

// One style per overlap depth so stacked bands (e.g. this year's campaign plus last
// year's shifted onto the same weeks) read as separate periods: each band's fill,
// borders and label share a hue. Fills are the 500-shade at ~8% alpha.
const BAND_STYLES = [
  { fill: "#F59E0B14", border: "--color-amber-300", label: "--color-amber-600" },
  { fill: "#D946EF14", border: "--color-fuchsia-400", label: "--color-fuchsia-500" },
  { fill: "#06B6D414", border: "--color-cyan-400", label: "--color-cyan-500" },
]

export function toAdjustmentPlotBands(ranges: BandRange[]): PlotBand[] {
  // Current-window bands sort before shifted past-year ones so a live campaign
  // always keeps the base amber regardless of what history stacks under it.
  const sorted = [...ranges].sort(
    (a, b) => (a.yearsShifted ?? 0) - (b.yearsShifted ?? 0) || a.from - b.from,
  )
  return sorted.map((range, index) => {
    const overlaps = sorted
      .slice(0, index)
      .filter((other) => other.to > range.from && other.from < range.to).length
    const style = BAND_STYLES[overlaps % BAND_STYLES.length]!
    return {
      ...range,
      color: style.fill,
      borderColor: getTailwindValue(style.border),
      textColor: getTailwindValue(style.label),
      // Stack overlapping labels instead of overprinting them.
      labelY: 20 + overlaps * 16,
    }
  })
}

export function getCollectionPlotBands(collections: IVariantCollection[]) {
  return collections
    ?.filter((collection) => collection.startDate && collection.endDate)
    .map((collection) => ({
      text: collection.name,
      from: collection.startDate?.getTime() ?? 0,
      to: collection.endDate?.getTime() ?? 0,
      color: "#8B5CF61A",
      borderColor: getTailwindValue("--color-violet-300"),
      textColor: getTailwindValue("--color-violet-500"),
    }))
}
